<template>
  <LoadingOverlay :visible="overlayVisible" :message="overlayMessage" />

  <!-- Banner de dados desatualizados -->
  <div v-if="dataStale" class="stale-bar">
    <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5" stroke-linecap="round" stroke-linejoin="round">
      <path d="M10.29 3.86L1.82 18a2 2 0 0 0 1.71 3h16.94a2 2 0 0 0 1.71-3L13.71 3.86a2 2 0 0 0-3.42 0z"/>
      <line x1="12" y1="9" x2="12" y2="13"/><line x1="12" y1="17" x2="12.01" y2="17"/>
    </svg>
    Sem conexão com o servidor — exibindo dados de <strong>{{ lastUpdate || '—' }}</strong>
    <button class="stale-bar-btn" @click="loadData">Tentar agora</button>
  </div>

  <!-- Barra de filtro global por time -->
  <div class="filter-bar" v-if="teamOptions.length">
    <div class="filter-bar-inner">

      <span class="filter-label">
        <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5" stroke-linecap="round" stroke-linejoin="round">
          <polygon points="22 3 2 3 10 12.46 10 19 14 21 14 12.46 22 3"/>
        </svg>
        Time
      </span>

      <!-- Dropdown multi-select -->
      <div class="flt-dropdown-wrap" ref="filterDropdownRef">
        <button
          class="flt-dropdown-btn"
          :class="{ open: filterOpen, selected: selectedTeams.length > 0 }"
          @click.stop="filterOpen = !filterOpen"
          :disabled="!isAdmin && userTeamIds.length > 0"
        >
          <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
            <path d="M17 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2"/>
            <circle cx="9" cy="7" r="4"/>
            <path d="M23 21v-2a4 4 0 0 0-3-3.87"/>
            <path d="M16 3.13a4 4 0 0 1 0 7.75"/>
          </svg>
          <span>{{ selectedTeams.length ? `${selectedTeams.length} time${selectedTeams.length > 1 ? 's' : ''}` : 'Todos os times' }}</span>
          <svg class="flt-chevron" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5" stroke-linecap="round" stroke-linejoin="round" v-if="isAdmin || !userTeamIds.length">
            <polyline points="6 9 12 15 18 9"/>
          </svg>
        </button>

        <transition name="dropdown-slide">
          <div v-if="filterOpen && (isAdmin || !userTeamIds.length)" class="flt-panel">
            <div class="flt-panel-head">
              <button class="flt-head-btn" @click="selectAllTeams">Todos</button>
              <span class="flt-head-count">{{ teamOptions.length }} times</span>
              <button class="flt-head-btn danger" @click="clearTeams">Limpar</button>
            </div>
            <div class="flt-panel-list">
              <label
                v-for="t in teamOptions"
                :key="t.id"
                class="flt-item"
                :class="{ checked: isTeamSelected(t.id), 'flt-item-semtime': t.id === -1 }"
                @click.prevent="toggleTeam(t.id)"
              >
                <span class="flt-checkbox" :class="{ checked: isTeamSelected(t.id) }">
                  <svg v-if="isTeamSelected(t.id)" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="3.5" stroke-linecap="round" stroke-linejoin="round">
                    <polyline points="20 6 9 17 4 12"/>
                  </svg>
                </span>
                <span class="flt-item-name">{{ t.name }}</span>
                <span class="flt-item-count">{{ teamConvCount(t.id) }}</span>
              </label>
            </div>
          </div>
        </transition>
      </div>

      <!-- Separador + Filtro de Etiquetas -->
      <span class="filter-sep"></span>

      <span class="filter-label">
        <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5" stroke-linecap="round" stroke-linejoin="round">
          <path d="M20.59 13.41l-7.17 7.17a2 2 0 0 1-2.83 0L2 12V2h10l8.59 8.59a2 2 0 0 1 0 2.82z"/>
          <line x1="7" y1="7" x2="7.01" y2="7"/>
        </svg>
        Etiqueta
      </span>

      <!-- Dropdown multi-select de etiquetas -->
      <div class="flt-dropdown-wrap" ref="labelFilterDropdownRef">
        <button
          class="flt-dropdown-btn"
          :class="{ open: labelFilterOpen, selected: selectedLabels.length > 0 }"
          @click.stop="labelFilterOpen = !labelFilterOpen"
          :disabled="!labelOptions.length"
        >
          <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
            <path d="M20.59 13.41l-7.17 7.17a2 2 0 0 1-2.83 0L2 12V2h10l8.59 8.59a2 2 0 0 1 0 2.82z"/>
            <line x1="7" y1="7" x2="7.01" y2="7"/>
          </svg>
          <span>{{ selectedLabels.length ? `${selectedLabels.length} etiqueta${selectedLabels.length > 1 ? 's' : ''}` : 'Todas as etiquetas' }}</span>
          <svg class="flt-chevron" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5" stroke-linecap="round" stroke-linejoin="round">
            <polyline points="6 9 12 15 18 9"/>
          </svg>
        </button>

        <transition name="dropdown-slide">
          <div v-if="labelFilterOpen && labelOptions.length" class="flt-panel">
            <div class="flt-panel-head">
              <button class="flt-head-btn" @click="selectAllLabels">Todos</button>
              <span class="flt-head-count">{{ labelOptions.length }} etiqueta{{ labelOptions.length !== 1 ? 's' : '' }}</span>
              <button class="flt-head-btn danger" @click="clearLabels">Limpar</button>
            </div>
            <div class="flt-panel-list">
              <label
                v-for="label in labelOptions"
                :key="label"
                class="flt-item"
                :class="{ checked: isLabelSelected(label) }"
                @click.prevent="toggleLabel(label)"
              >
                <span class="flt-checkbox" :class="{ checked: isLabelSelected(label) }">
                  <svg v-if="isLabelSelected(label)" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="3.5" stroke-linecap="round" stroke-linejoin="round">
                    <polyline points="20 6 9 17 4 12"/>
                  </svg>
                </span>
                <span class="flt-item-name">{{ label.replace(/_/g, ' ') }}</span>
                <span class="flt-item-count">{{ labelConvCount(label) }}</span>
              </label>
            </div>
          </div>
        </transition>
      </div>

      <!-- Separador + Filtro de Fluxo -->
      <span class="filter-sep"></span>

      <span class="filter-label">
        <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5" stroke-linecap="round" stroke-linejoin="round">
          <polyline points="22 12 18 12 15 21 9 3 6 12 2 12"/>
        </svg>
        Fluxo
      </span>

      <div class="flt-dropdown-wrap" ref="fluxoFilterDropdownRef">
        <button
          class="flt-dropdown-btn"
          :class="{ open: fluxoFilterOpen, selected: selectedFluxos.length > 0 }"
          @click.stop="fluxoFilterOpen = !fluxoFilterOpen"
          :disabled="!fluxoOptions.length"
        >
          <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
            <polyline points="22 12 18 12 15 21 9 3 6 12 2 12"/>
          </svg>
          <span>{{ selectedFluxos.length ? `${selectedFluxos.length} fluxo${selectedFluxos.length > 1 ? 's' : ''}` : 'Todos os fluxos' }}</span>
          <svg class="flt-chevron" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5" stroke-linecap="round" stroke-linejoin="round">
            <polyline points="6 9 12 15 18 9"/>
          </svg>
        </button>

        <transition name="dropdown-slide">
          <div v-if="fluxoFilterOpen && fluxoOptions.length" class="flt-panel">
            <div class="flt-panel-head">
              <button class="flt-head-btn" @click="selectAllFluxos">Todos</button>
              <span class="flt-head-count">{{ fluxoOptions.length }} fluxo{{ fluxoOptions.length !== 1 ? 's' : '' }}</span>
              <button class="flt-head-btn danger" @click="clearFluxos">Limpar</button>
            </div>
            <div class="flt-panel-list">
              <label
                v-for="f in fluxoOptions"
                :key="f.id"
                class="flt-item"
                :class="{ checked: isFluxoSelected(f.id) }"
                @click.prevent="toggleFluxo(f.id)"
              >
                <span class="flt-checkbox" :class="{ checked: isFluxoSelected(f.id) }">
                  <svg v-if="isFluxoSelected(f.id)" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="3.5" stroke-linecap="round" stroke-linejoin="round">
                    <polyline points="20 6 9 17 4 12"/>
                  </svg>
                </span>
                <span class="flt-item-name">{{ f.name }}</span>
                <span class="flt-item-count">{{ fluxoConvCount(f.id) }}</span>
              </label>
            </div>
          </div>
        </transition>
      </div>

      <!-- Separador + Filtro de Status -->
      <span class="filter-sep"></span>

      <span class="filter-label">
        <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5" stroke-linecap="round" stroke-linejoin="round">
          <circle cx="12" cy="12" r="10"/>
          <polyline points="12 6 12 12 16 14"/>
        </svg>
        Status
      </span>
      <MultiSelectFilter
        :options="STATUS_FILTER_OPTIONS"
        :model-value="selectedStatuses"
        placeholder="Todos os status"
        @update:model-value="selectedStatuses = $event.map(String)"
      />

      <!-- Separador + Tolerância -->
      <span class="filter-sep"></span>
      <span class="filter-label">
        <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5" stroke-linecap="round" stroke-linejoin="round">
          <circle cx="12" cy="12" r="10"/>
          <polyline points="12 6 12 12 16 14"/>
        </svg>
        Tolerância
      </span>
      <div class="tol-control" title="Tempo mínimo de espera para exibir na aba Aguardando e gerar alerta">
        <input
          type="number"
          class="tol-input"
          v-model.number="toleranceMinutes"
          min="1"
          max="240"
          @blur="clampTolerance"
        />
        <span class="tol-unit">min</span>
      </div>

    </div>

    <!-- Linha de chips ativos: separada dos controles para não embolar quando há muita coisa selecionada -->
    <transition name="fade-filter">
      <div v-if="selectedTeams.length || selectedLabels.length || selectedFluxos.length" class="filter-chips-bar">
        <div class="filter-chips-bar-inner">

          <transition-group name="chip-anim" tag="div" class="flt-chips">
            <span v-for="id in selectedTeams" :key="'t' + id" class="flt-chip">
              {{ teamName(id) }}
              <button class="flt-chip-x" @click="toggleTeam(id)" v-if="isAdmin || !userTeamIds.length">
                <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="3" stroke-linecap="round" stroke-linejoin="round">
                  <line x1="18" y1="6" x2="6" y2="18"/><line x1="6" y1="6" x2="18" y2="18"/>
                </svg>
              </button>
            </span>
          </transition-group>

          <transition-group name="chip-anim" tag="div" class="flt-chips">
            <span v-for="label in selectedLabels" :key="'l' + label" class="flt-chip flt-chip-label">
              {{ label.replace(/_/g, ' ') }}
              <button class="flt-chip-x" @click="toggleLabel(label)">
                <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="3" stroke-linecap="round" stroke-linejoin="round">
                  <line x1="18" y1="6" x2="6" y2="18"/><line x1="6" y1="6" x2="18" y2="18"/>
                </svg>
              </button>
            </span>
          </transition-group>

          <transition-group name="chip-anim" tag="div" class="flt-chips">
            <span v-for="id in selectedFluxos" :key="'f' + id" class="flt-chip flt-chip-fluxo">
              {{ fluxoLabel(id) }}
              <button class="flt-chip-x" @click="toggleFluxo(id)">
                <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="3" stroke-linecap="round" stroke-linejoin="round">
                  <line x1="18" y1="6" x2="6" y2="18"/><line x1="6" y1="6" x2="18" y2="18"/>
                </svg>
              </button>
            </span>
          </transition-group>

          <div v-if="(selectedTeams.length && (isAdmin || !userTeamIds.length)) || selectedLabels.length || selectedFluxos.length" class="filter-active-info">
            <span class="filter-active-badge">
              <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5" stroke-linecap="round" stroke-linejoin="round">
                <polyline points="20 6 9 17 4 12"/>
              </svg>
              {{ filteredConversations.length }} conversa{{ filteredConversations.length !== 1 ? 's' : '' }}
            </span>
            <button class="filter-clear" @click="clearTeams(); clearLabels(); clearFluxos()">
              <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5" stroke-linecap="round" stroke-linejoin="round">
                <line x1="18" y1="6" x2="6" y2="18"/><line x1="6" y1="6" x2="18" y2="18"/>
              </svg>
              Limpar tudo
            </button>
          </div>

        </div>
      </div>
    </transition>

  </div>

  <main>
    <KpiGrid
      v-if="can('dashboard.viewKpis')"
      :total="kpi.total"
      :open="kpi.open"
      :em-avaliacao="kpi.emAvaliacao"
      :unassigned="kpi.unassigned"
      :resolved="kpi.resolved"
      :res-pct="kpi.resPct"
      :periodo="visaoGeralPeriodo"
      :loading="loading"
      :scope-ready="scopeReady"
      :month-pending-count="monthOnlyCount"
      @update:periodo="setVisaoGeralPeriodo"
    />

    <div class="cols">
      <TabsPanel
        :conversations="filteredConversations"
        :all-agents="roleFilteredAgents"
        :agent-teams-map="agentTeamsMap"
        :tolerance-minutes="toleranceMinutes"
        :scope-ready="scopeReady"
        @show-popup="showPopup"
        @hide-popup="hidePopup"
      />
      <SidePanel
        :conversations="filteredConversations"
        :agent-teams-map="agentTeamsMap"
        :scope-ready="scopeReady"
        @show-popup="showPopup"
        @hide-popup="hidePopup"
        @open-agent-summary="(agent, convs) => agentModalData = { agent: { ...agent, teamIds: agentTeamsMap[agent.id] ?? [] }, conversations: convs }"
      />
    </div>

    <ConversationsPanel
      :conversations="filteredConversations"
      :loading="loading"
      :scope-ready="scopeReady"
      :changed-ids="changedIds"
      @show-popup="showPopup"
      @hide-popup="hidePopup"
      @open-chat="openFloatChat"
      @open-history="(conv) => historyConv = conv"
    />

    <!-- Modais -->
    <AgentSummaryModal
      v-if="agentModalData"
      :data="{ ...agentModalData.agent, conversations: agentModalData.conversations }"
      @close="agentModalData = null"
      @open-chat="openFloatChat"
    />
    <ContactHistoryModal
      v-if="historyConv"
      :contact="historyConv.contact"
      :account-id="CHATWOOT_ACCOUNT_ID"
      :access-token="authUser?.access_token ?? ''"
      @close="historyConv = null"
      @open-chat="(c) => { historyConv = null; openFloatChat(c) }"
    />
  </main>

  <footer>
    <p>Wootdash &nbsp;·&nbsp; <strong>ISBET</strong> &nbsp;·&nbsp; <span>Tecnologia ISBET</span></p>
  </footer>

  <!-- Global overlays -->
  <div id="toast" :class="{ show: toastVisible, err: toastError }">{{ toastMessage }}</div>
  <AvatarPopup ref="avPopupRef" />

  <!-- Notification Stack -->
  <transition-group name="notif-slide" tag="div" class="notif-stack">
    <div v-for="n in activeNotifications" :key="n.uid" class="notif-card">
      <div class="notif-card-icon">
        <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5" stroke-linecap="round" stroke-linejoin="round">
          <circle cx="12" cy="12" r="10"/>
          <polyline points="12 6 12 12 16 14"/>
        </svg>
      </div>
      <div class="notif-card-body">
        <div class="notif-card-title">
          <strong>{{ n.contactName }}</strong> aguarda resposta
        </div>
        <div class="notif-card-wait">{{ n.waitLabel }} esperando</div>
        <div class="notif-card-meta">
          <span v-if="n.team" class="notif-meta-tag">
            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
              <path d="M17 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2"/>
              <circle cx="9" cy="7" r="4"/>
              <path d="M23 21v-2a4 4 0 0 0-3-3.87"/>
              <path d="M16 3.13a4 4 0 0 1 0 7.75"/>
            </svg>
            {{ n.team }}
          </span>
          <span class="notif-meta-tag" :class="{ 'notif-muted': !n.agent }">
            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
              <path d="M20 21v-2a4 4 0 0 0-4-4H8a4 4 0 0 0-4 4v2"/>
              <circle cx="12" cy="7" r="4"/>
            </svg>
            {{ n.agent || 'Sem agente' }}
          </span>
        </div>
      </div>
      <div class="notif-card-actions">
        <button class="notif-btn-go" @click="requestOpenChat(n.convId); dismissNotif(n.uid)">Ver Conversa</button>
        <button class="notif-btn-close" @click="dismissNotif(n.uid)" title="Fechar">
          <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="3" stroke-linecap="round" stroke-linejoin="round">
            <line x1="18" y1="6" x2="6" y2="18"/>
            <line x1="6" y1="6" x2="18" y2="18"/>
          </svg>
        </button>
      </div>
    </div>
  </transition-group>
</template>

<script setup lang="ts">
import { ref, computed, onMounted, onUnmounted, watch } from 'vue'
import { useStore } from 'vuex'
import { usePermission } from '@/composables/usePermission'
import { syncUserRole, useAdmin, resolveRoleScope } from '@/composables/useAdmin'
import type { Conversation, PopupData } from '@/types/dashboard'
import { testNotification } from '@/services/notifications'
import { fluxoLabel } from '@/composables/useHelpers'
import { isFluxoHidden } from '@/composables/useHiddenFluxos'
import { requestOpenChat, setConvNavList } from '@/composables/useChatTrigger'
import { useFloatChats } from '@/composables/useFloatChats'

import LoadingOverlay      from '@/components/dashboard/LoadingOverlay.vue'
import {
  headerLoading, headerLastUpdate, headerMyCount, headerMyOpenCount, headerMyTeamFilter, registerHeaderCallbacks,
} from '@/composables/useHeaderState'
import KpiGrid             from '@/components/dashboard/KpiGrid.vue'
import MultiSelectFilter   from '@/components/dashboard/MultiSelectFilter.vue'
import TabsPanel           from '@/components/dashboard/TabsPanel.vue'
import SidePanel           from '@/components/dashboard/SidePanel.vue'
import ConversationsPanel  from '@/components/dashboard/ConversationsPanel.vue'
import AvatarPopup         from '@/components/dashboard/AvatarPopup.vue'
import AgentSummaryModal   from '@/components/dashboard/AgentSummaryModal.vue'
import ContactHistoryModal from '@/components/dashboard/ContactHistoryModal.vue'
import { useConversationStore } from '@/composables/useConversationStore'
// Motor de dados do Dashboard (polling, badge "Mês", notificações) — vive fora
// do ciclo de vida desta view (ver useDashboardEngine.ts) pra continuar
// rodando mesmo quando o usuário sai desta tela; iniciado uma vez em App.vue.
import {
  allAgents, allTeamsWithMembers, dataStale, changedIds, visaoGeralPeriodo, toleranceMinutes,
  toastMessage, toastVisible, toastError, activeNotifications, scopeReady,
  toast, clampTolerance, dismissNotif, roleFilteredConversations, monthOnlyCount, passesFluxoFilter,
  setVisaoGeralPeriodo, loadData, startDashboardEngine,
} from '@/composables/useDashboardEngine'

const CHATWOOT_ACCOUNT_ID = 3

const store  = useStore()

const authUser    = computed(() => store.getters.authUser)
const isAdmin     = computed(() => store.getters.isAdmin)
const userTeamIds = computed<number[]>(() => store.getters.userTeamIds)

const { can } = usePermission()
const { hasPermission } = useAdmin()
const { open: openFloatChat } = useFloatChats()

/* ── State ─────────────────────────────────── */
const { conversations } = useConversationStore()
const selectedTeams      = ref<number[]>([])
const filterOpen         = ref(false)
const filterDropdownRef  = ref<HTMLElement | null>(null)
const selectedLabels     = ref<string[]>([])
const labelFilterOpen    = ref(false)
const labelFilterDropdownRef = ref<HTMLElement | null>(null)
const selectedFluxos     = ref<number[]>([])
const fluxoFilterOpen    = ref(false)
const fluxoFilterDropdownRef = ref<HTMLElement | null>(null)
// Fase 1 do plano de filtro de status (docs/planos/PLANO-FILTRO-STATUS-VISAO-GERAL.md):
// default só "Aberta" — ainda client-side, a busca de rede continua trazendo tudo.
// id/name no formato esperado por MultiSelectFilter.vue (mesmo componente genérico
// usado em "Esconder Fluxo", DashHeader.vue).
const STATUS_FILTER_OPTIONS: { id: string; name: string }[] = [
  { id: 'open',     name: 'Aberta' },
  { id: 'pending',  name: 'Pendente' },
  { id: 'resolved', name: 'Resolvida' },
]
const selectedStatuses    = ref<string[]>(['open'])
const loading            = headerLoading
const overlayVisible     = ref(true)
const overlayMessage     = ref('Verificando Permissões…')

/* ── My-team filter ──────────────────────────── */
const myTeamFilter = headerMyTeamFilter

/* ── Agent summary modal ─────────────────────── */
const agentModalData = ref<{ agent: any; conversations: Conversation[] } | null>(null)

/* ── Contact history modal ───────────────────── */
const historyConv = ref<Conversation | null>(null)

const lastUpdate         = headerLastUpdate
const avPopupRef         = ref<InstanceType<typeof AvatarPopup> | null>(null)

/* ── Aba Agentes — mesmo filtro por permissão (Regra Geral) ─────── */
const agentTeamsMap = computed(() => {
  const map: Record<number, number[]> = {}
  for (const t of allTeamsWithMembers.value)
    for (const mid of t.memberIds) {
      if (!map[mid]) map[mid] = []
      map[mid].push(t.id)
    }
  return map
})

const roleFilteredAgents = computed(() => {
  const uid   = authUser.value?.id
  const scope = resolveRoleScope(uid ?? 0, userTeamIds.value)

  // Administrador / EC - ISBET — travado pelo nome, ignora tudo abaixo
  if (scope.unrestricted) return allAgents.value

  // 1ª/2ª prioridade — Agente vê colegas do(s) próprio(s) time(s); Supervisor vê agentes do(s) Time(s) real(is) que supervisiona
  if (scope.hasSomenteAgente || scope.hasAgenteETime) {
    return allAgents.value.filter(a =>
      a.id === uid || (scope.tids.length > 0 && agentTeamsMap.value[a.id]?.some(tid => scope.tids.includes(tid)))
    )
  }

  // 3ª prioridade — Todas as Atribuições
  if (scope.hasTodos) return allAgents.value

  // Fallback (sem regra geral definida): filtro restritivo (próprio + times)
  return allAgents.value.filter(a =>
    a.id === uid || (scope.tids.length > 0 && agentTeamsMap.value[a.id]?.some(tid => scope.tids.includes(tid)))
  )
})

/* ── Opções de time — construídas da visão já filtrada ── */
const teamOptions = computed(() => {
  const map = new Map<number, string>()
  roleFilteredConversations.value.forEach(c => {
    if (c.team) map.set(c.team.id, c.team.name)
  })
  const list = Array.from(map.entries())
    .map(([id, name]) => ({ id, name }))
    .sort((a, b) => a.name.localeCompare(b.name))

  if (roleFilteredConversations.value.some(c => !c.team)) {
    list.push({ id: -1, name: 'TypeBot' })
  }
  return list
})

// Pré-seleciona todos os times disponíveis para usuários sem visaoTodos
watch(
  [userTeamIds, teamOptions],
  ([_tids, opts], prev) => {
    const uid = authUser.value?.id ?? 0
    const scope = resolveRoleScope(uid, userTeamIds.value)
    const viewAll = scope.unrestricted ||
                    (!scope.hasSomenteAgente && !scope.hasAgenteETime && scope.hasTodos)
    const currIds = (opts as { id: number }[]).map(t => t.id)

    if (!viewAll && !selectedTeams.value.length) {
      selectedTeams.value = currIds
      return
    }

    // "Todos" (ou a pré-seleção acima) grava os ids concretos que existiam na hora —
    // sem isso, um time/bucket novo (ex: "TypeBot" = conversa sem time) que apareça
    // DEPOIS fica de fora do filtro pra sempre, mesmo o usuário "achando" que está
    // vendo tudo. Se a seleção atual já cobria 100% dos ids anteriores, trata como
    // "modo ver tudo" e absorve os novos ids automaticamente.
    const prevOpts = (prev?.[1] as { id: number }[] | undefined) ?? []
    const prevIds  = prevOpts.map(t => t.id)
    const wasShowingAll = prevIds.length > 0 && prevIds.every(id => selectedTeams.value.includes(id))
    if (wasShowingAll) {
      const newIds = currIds.filter(id => !selectedTeams.value.includes(id))
      if (newIds.length) selectedTeams.value = [...selectedTeams.value, ...newIds]
    }
  },
  { immediate: true },
)

const labelOptions = computed(() => {
  const all = [...new Set(roleFilteredConversations.value.flatMap(c => c.labels))].filter(Boolean)
  return all.sort((a, b) => a.localeCompare(b))
})

const fluxoOptions = computed(() => {
  const ids = [...new Set(roleFilteredConversations.value
    .map(c => c.fluxo_id)
    .filter((id): id is number => id !== null)
  )].sort((a, b) => a - b)
  return ids.map(id => ({ id, name: fluxoLabel(id) }))
})

const filteredConversations = computed(() => {
  let result = roleFilteredConversations.value.filter(c => !isFluxoHidden(c.fluxo_id))

  if (selectedTeams.value.length) {
    result = result.filter(c => {
      if (c.team  && selectedTeams.value.includes(c.team.id)) return true
      if (!c.team && selectedTeams.value.includes(-1))        return true
      return false
    })
  }

  if (selectedLabels.value.length) {
    result = result.filter(c =>
      selectedLabels.value.some(label => c.labels.includes(label))
    )
  }

  if (selectedFluxos.value.length) {
    result = result.filter(c =>
      c.fluxo_id !== null && selectedFluxos.value.includes(c.fluxo_id)
    )
  }

  // Lista vazia = sem restrição (mesma convenção dos filtros acima) — inclui
  // até "snoozed" (Adiada), que não tem checkbox próprio no multiselect.
  if (selectedStatuses.value.length) {
    result = result.filter(c => selectedStatuses.value.includes(c.status))
  }

  if (myTeamFilter.value && authUser.value) {
    const uid  = authUser.value.id
    const tids = userTeamIds.value
    result = result.filter(c =>
      c.assignee?.id === uid || (c.team && tids.includes(c.team.id))
    )
  }

  result = result.filter(c => passesFluxoFilter(c.fluxo_id))

  return result
})

watch(filteredConversations, convs => setConvNavList(convs.map((c: any) => c.id)), { immediate: true })

const myCount = computed(() =>
  conversations.value.filter(c => c.assignee?.id === authUser.value?.id).length
)
watch(myCount, v => { headerMyCount.value = v }, { immediate: true })

const myOpenCount = computed(() =>
  conversations.value.filter(c => c.assignee?.id === authUser.value?.id && c.status === 'open').length
)
watch(myOpenCount, v => { headerMyOpenCount.value = v }, { immediate: true })

function isTeamSelected(id: number)  { return selectedTeams.value.includes(id) }
function teamName(id: number)        { return teamOptions.value.find(t => t.id === id)?.name || '' }
function teamConvCount(id: number)   {
  if (id === -1) return roleFilteredConversations.value.filter(c => !c.team).length
  return roleFilteredConversations.value.filter(c => c.team?.id === id).length
}

function toggleTeam(id: number) {
  const idx = selectedTeams.value.indexOf(id)
  if (idx === -1) selectedTeams.value.push(id)
  else selectedTeams.value.splice(idx, 1)
}
function selectAllTeams() { selectedTeams.value = teamOptions.value.map(t => t.id) }
function clearTeams()     { selectedTeams.value = [] }

function isLabelSelected(label: string)  { return selectedLabels.value.includes(label) }
function toggleLabel(label: string) {
  const idx = selectedLabels.value.indexOf(label)
  if (idx === -1) selectedLabels.value.push(label)
  else            selectedLabels.value.splice(idx, 1)
}
function selectAllLabels() { selectedLabels.value = [...labelOptions.value] }
function clearLabels()     { selectedLabels.value = [] }
function labelConvCount(label: string) {
  return roleFilteredConversations.value.filter(c => c.labels.includes(label)).length
}

function isFluxoSelected(id: number)  { return selectedFluxos.value.includes(id) }
function toggleFluxo(id: number) {
  const idx = selectedFluxos.value.indexOf(id)
  if (idx === -1) selectedFluxos.value.push(id)
  else            selectedFluxos.value.splice(idx, 1)
}
function selectAllFluxos() { selectedFluxos.value = fluxoOptions.value.map(f => f.id) }
function clearFluxos()     { selectedFluxos.value = [] }
function fluxoConvCount(id: number) {
  return roleFilteredConversations.value.filter(c => c.fluxo_id === id).length
}


function onDocClick(e: MouseEvent) {
  if (filterDropdownRef.value && !filterDropdownRef.value.contains(e.target as Node)) {
    filterOpen.value = false
  }
  if (labelFilterDropdownRef.value && !labelFilterDropdownRef.value.contains(e.target as Node)) {
    labelFilterOpen.value = false
  }
  if (fluxoFilterDropdownRef.value && !fluxoFilterDropdownRef.value.contains(e.target as Node)) {
    fluxoFilterOpen.value = false
  }
}

watch(teamOptions, (opts) => {
  const valid = opts.map(t => t.id)
  selectedTeams.value = selectedTeams.value.filter(id => valid.includes(id))
})

/* ── KPI ────────────────────────────────────── */
const kpi = computed(() => {
  const c    = filteredConversations.value
  // Conversas abertas com a etiqueta "avaliacao_atendimento" (fluxo interno
  // de avaliação/CSAT do bot, mesmo critério de TabsPanel.vue/useMyConversations.ts)
  // saem do KPI "Abertas" e viram o KPI "Em Avaliação" — não são atendimento
  // real em andamento, só aguardando resposta de CSAT.
  const open = c.filter(x => x.status === 'open' && !x.labels.includes('avaliacao_atendimento')).length
  const emAvaliacao = c.filter(x => x.status === 'open' && x.labels.includes('avaliacao_atendimento')).length
  const resolved = c.filter(x => x.status === 'resolved').length
  const total = c.length
  return {
    total,
    open,
    emAvaliacao,
    unassigned: c.filter(x => !x.assignee && (x.status === 'open' || x.status === 'pending')).length,
    resolved,
    resPct:  total > 0 ? Math.round(resolved / total * 100) : 0,
  }
})

/* ── Avatar popup ───────────────────────────── */
function showPopup(data: PopupData, el: HTMLElement) {
  avPopupRef.value?.show(data, el)
}
function hidePopup() {
  avPopupRef.value?.hide()
}

/* ── Report generation ──────────────────────── */
function generateReport() {
  const convs = filteredConversations.value
  if (!convs.length) { toast('Sem dados para exportar', true); return }

  const filterLabel = selectedTeams.value.length
    ? selectedTeams.value.map(id => teamName(id)).join(', ')
    : null

  const now     = new Date()
  const dateStr = now.toLocaleDateString('pt-BR', { day: '2-digit', month: '2-digit', year: 'numeric' })
  const timeStr = now.toLocaleTimeString('pt-BR', { hour: '2-digit', minute: '2-digit' })

  const total      = convs.length
  const openN      = convs.filter(c => c.status === 'open').length
  const resolvedN  = convs.filter(c => c.status === 'resolved').length
  const pendingN   = convs.filter(c => c.status === 'pending').length
  const unassigned = convs.filter(c => !c.assignee).length
  const resPct     = total > 0 ? Math.round(resolvedN / total * 100) : 0
  const unread     = convs.reduce((s, c) => s + c.unread_count, 0)

  const teamMap: Record<string, any> = {}
  convs.forEach(c => {
    const k = c.team?.name || 'TypeBot'
    if (!teamMap[k]) teamMap[k] = { total: 0, open: 0, resolved: 0, pending: 0 }
    teamMap[k].total++
    if (c.status === 'open')     teamMap[k].open++
    if (c.status === 'resolved') teamMap[k].resolved++
    if (c.status === 'pending')  teamMap[k].pending++
  })
  const teams = Object.entries(teamMap).sort((a, b) => b[1].total - a[1].total)

  const agMap: Record<number, any> = {}
  convs.forEach(c => {
    if (!c.assignee) return
    const a = c.assignee
    if (!agMap[a.id]) agMap[a.id] = { ...a, total: 0, open: 0, resolved: 0, pending: 0, resolveSecs: [] }
    agMap[a.id].total++
    if (c.status === 'open')     agMap[a.id].open++
    if (c.status === 'pending')  agMap[a.id].pending++
    if (c.status === 'resolved') {
      agMap[a.id].resolved++
      if (c.resolved_at && c.created_at && c.resolved_at > c.created_at)
        agMap[a.id].resolveSecs.push(c.resolved_at - c.created_at)
    }
  })
  const agents = Object.values(agMap).sort((a, b) => b.total - a.total).map(a => ({
    ...a,
    avgResolve: a.resolveSecs.length ? Math.round(a.resolveSecs.reduce((s: number, v: number) => s + v, 0) / a.resolveSecs.length) : 0,
  }))

  function fmtDur(secs: number) {
    if (!secs || secs <= 0) return '—'
    const m = Math.floor(secs / 60), h = Math.floor(m / 60), d = Math.floor(h / 24)
    if (d > 0) return `${d}d ${h % 24}h`
    if (h > 0) return `${h}h ${m % 60 > 0 ? (m % 60) + 'min' : ''}`.trim()
    if (m > 0) return `${m}min`
    return '<1min'
  }

  const html = `<!DOCTYPE html>
<html lang="pt-BR">
<head>
<meta charset="UTF-8"><meta name="viewport" content="width=device-width,initial-scale=1">
<title>Relatório Wootdash — ISBET ${dateStr}</title>
<link href="https://fonts.googleapis.com/css2?family=Poppins:wght@300;400;500;600;700;800&display=swap" rel="stylesheet">
<style>
*,*::before,*::after{box-sizing:border-box;margin:0;padding:0}
body{font-family:'Poppins',Arial,sans-serif;background:#F4F5F7;color:#1A1A1A;font-size:13px;-webkit-print-color-adjust:exact;print-color-adjust:exact}
.r-page{max-width:1100px;margin:0 auto;padding:32px 28px 56px}
.r-header{background:linear-gradient(135deg,#BC0C24 0%,#73010F 100%);border-radius:14px;padding:28px 32px;display:flex;align-items:center;justify-content:space-between;margin-bottom:28px;box-shadow:0 6px 24px rgba(115,1,15,.3)}
.r-title{color:#fff;font-size:22px;font-weight:800;line-height:1.2}
.r-subtitle{color:rgba(255,255,255,.6);font-size:11.5px;margin-top:3px}
.r-date{color:rgba(255,255,255,.9);font-size:13px;font-weight:600;text-align:right}
.r-date small{display:block;color:rgba(255,255,255,.45);font-size:10.5px;margin-top:2px;font-weight:400}
.r-kpi-row{display:grid;grid-template-columns:repeat(auto-fit,minmax(160px,1fr));gap:14px;margin-bottom:24px}
.r-kpi{background:#fff;border-radius:12px;border:1px solid #E9EAEC;padding:18px 16px;box-shadow:0 1px 4px rgba(0,0,0,.05)}
.r-kpi-v{font-size:32px;font-weight:800;line-height:1;margin-bottom:4px}
.r-kpi-l{font-size:11px;font-weight:600;color:#6B6B6B;text-transform:uppercase;letter-spacing:.5px}
.r-sec-title{font-size:10.5px;font-weight:700;text-transform:uppercase;letter-spacing:1.6px;color:#6B6B6B;margin-bottom:14px;display:flex;align-items:center;gap:8px}
.r-sec-title::after{content:'';flex:1;height:1px;background:#E5E7EB}
.r-card{background:#fff;border-radius:12px;border:1px solid #E9EAEC;box-shadow:0 1px 4px rgba(0,0,0,.05);overflow:hidden;margin-bottom:20px}
table{width:100%;border-collapse:collapse;font-size:12px}
thead tr{background:#F9FAFB}
th{padding:9px 14px;text-align:left;font-size:10.5px;font-weight:700;text-transform:uppercase;letter-spacing:.7px;color:#6B6B6B;white-space:nowrap;border-bottom:1px solid #E9EAEC}
td{padding:10px 14px;border-bottom:1px solid #F3F4F6;color:#3D3D3D;vertical-align:middle}
tr:last-child td{border-bottom:none}
.r-footer{text-align:center;padding:24px 0 0;color:#9CA3AF;font-size:11px}
@media print{body{background:#fff}.r-page{padding:0}}
</style>
</head>
<body>
<div class="r-page">
  <div class="r-header">
    <div>
      <div class="r-title">Relatório Wootdash</div>
      <div class="r-subtitle">ISBET — ${filterLabel ? `Filtrado por: ${filterLabel}` : 'Todos os times'}</div>
    </div>
    <div class="r-date">${dateStr} ${timeStr}<small>Gerado automaticamente</small></div>
  </div>
  <div class="r-kpi-row">
    <div class="r-kpi"><div class="r-kpi-v" style="color:#BC0C24">${total}</div><div class="r-kpi-l">Total</div></div>
    <div class="r-kpi"><div class="r-kpi-v" style="color:#0369A1">${openN}</div><div class="r-kpi-l">Abertas</div></div>
    <div class="r-kpi"><div class="r-kpi-v" style="color:#1F8C5A">${resolvedN}</div><div class="r-kpi-l">Resolvidas (${resPct}%)</div></div>
    <div class="r-kpi"><div class="r-kpi-v" style="color:#FF6600">${pendingN}</div><div class="r-kpi-l">Pendentes</div></div>
    <div class="r-kpi"><div class="r-kpi-v" style="color:#BC0C24">${unassigned}</div><div class="r-kpi-l">Sem agente</div></div>
    <div class="r-kpi"><div class="r-kpi-v" style="color:#021627">${unread}</div><div class="r-kpi-l">Não lidas</div></div>
  </div>
  <div class="r-sec-title">Por Time</div>
  <div class="r-card">
    <table>
      <thead><tr><th>Time</th><th>Total</th><th>Abertas</th><th>Resolvidas</th><th>Pendentes</th></tr></thead>
      <tbody>${teams.map(([name, d]) => `<tr><td><strong>${name}</strong></td><td>${d.total}</td><td>${d.open}</td><td>${d.resolved}</td><td>${d.pending}</td></tr>`).join('')}</tbody>
    </table>
  </div>
  <div class="r-sec-title">Por Agente</div>
  <div class="r-card">
    <table>
      <thead><tr><th>Agente</th><th>Total</th><th>Abertas</th><th>Resolvidas</th><th>TMR</th></tr></thead>
      <tbody>${agents.map(a => `<tr><td><strong>${a.name}</strong></td><td>${a.total}</td><td>${a.open}</td><td>${a.resolved}</td><td>${a.avgResolve > 0 ? fmtDur(a.avgResolve) : '—'}</td></tr>`).join('')}</tbody>
    </table>
  </div>
  <div class="r-footer"><strong>ISBET</strong> — Wootdash</div>
</div>
</body>
</html>`

  const win = window.open('', '_blank')
  if (win) { win.document.write(html); win.document.close() }
}

onMounted(async () => {
  registerHeaderCallbacks({
    onExport:    generateReport,
    onTestNotif: testNotification,
  })
  document.addEventListener('click', onDocClick)
  // Overlay cobre só a verificação de permissões (rápida, local) — não espera
  // mais o motor (useDashboardEngine.ts) terminar a primeira carga de dados.
  // Fase 3 do plano de carregamento progressivo (docs/planos/PLANO-SKELETON-DASHBOARD.md):
  // KPIs/grade já mostram skeleton próprio enquanto `scopeReady` não vira
  // `true` (ver KpiGrid.vue/ConversationsPanel.vue) — a tela inteira não
  // precisa mais ficar escondida atrás de um overlay de tela cheia esperando
  // isso. Se o motor já rodou antes (ex.: usuário navegou aqui vindo de
  // outra tela), startDashboardEngine() resolve na hora — chamado aqui só de
  // garantia (idempotente, mesma promise que App.vue já dispara no login).
  overlayVisible.value = true
  overlayMessage.value  = 'Verificando permissões…'
  // Fase 1 do plano de carregamento pós-login (PLANO-OTIMIZACAO-CARREGAMENTO-DASHBOARD.md):
  // loadProfiles()/applyDBProfiles() já rodaram em initAuthenticatedSession()
  // (login ao vivo, LoginView.vue) ou main.ts::bootstrap() (sessão restaurada
  // a frio) — os dois caminhos terminam ANTES desta view montar (login espera
  // a promise antes de navegar; bootstrap() só monta a app depois). Refazer
  // aqui era um round-trip n8n redundante, sempre com o mesmo resultado.
  if (authUser.value) syncUserRole(authUser.value.id, authUser.value.role)

  overlayVisible.value = false
  startDashboardEngine()
})

onUnmounted(() => {
  document.removeEventListener('click', onDocClick)
})
</script>
